import { useEffect, useMemo, useState } from "react";
import { useAdminVocabStore } from "../../state/adminVocabStore";
import { DangerDialog } from "./DangerDialog";
import { VocabFormDialog } from "./VocabFormDialog";
import type { AdminVocabRow } from "../../lib/api";

const PAGE_SIZE = 25;

type ExamplesFilter = "all" | "with" | "without" | "deleted";

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
  return new Date(iso).toISOString().slice(0, 10);
}

function rowMatches(row: AdminVocabRow, query: string): boolean {
  if (query.length === 0) return true;
  const q = query.toLowerCase();
  return (
    row.english.toLowerCase().includes(q) ||
    row.german.toLowerCase().includes(q) ||
    (row.abbreviation?.toLowerCase().includes(q) ?? false)
  );
}

function matchesExamplesFilter(row: AdminVocabRow, f: ExamplesFilter): boolean {
  if (f === "deleted") return row.deleted_at !== null;
  if (row.deleted_at !== null) return false;
  if (f === "all") return true;
  if (f === "with") return row.examples.length > 0;
  if (f === "without") return row.examples.length === 0;
  return true;
}

/**
 * Admin tab for vocabulary management (issue #74). Lists every row in the
 * vocab table (incl. soft-deleted), with client-side search/filter and
 * server-bound CRUD via `useAdminVocabStore`. Implements the mockup at
 * docs/Design-Kits/vocab-admin-mockup.html.
 */
export function AdminVocabTab(): JSX.Element {
  const status = useAdminVocabStore((s) => s.status);
  const rows = useAdminVocabStore((s) => s.rows);
  const error = useAdminVocabStore((s) => s.error);
  const load = useAdminVocabStore((s) => s.load);
  const softDelete = useAdminVocabStore((s) => s.softDelete);
  const restore = useAdminVocabStore((s) => s.restore);
  const resetToBaseline = useAdminVocabStore((s) => s.resetToBaseline);

  const [query, setQuery] = useState("");
  const [letter, setLetter] = useState<string>("");
  const [examplesFilter, setExamplesFilter] = useState<ExamplesFilter>("all");
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState<AdminVocabRow | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);

  useEffect(() => {
    if (status === "idle") void load();
  }, [status, load]);

  // Reset to page 1 whenever filters change so the user doesn't end up on
  // an empty page index.
  useEffect(() => {
    setPage(1);
  }, [query, letter, examplesFilter]);

  const lettersInUse = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      if (row.deleted_at === null) set.add(row.letter);
    }
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (!matchesExamplesFilter(row, examplesFilter)) return false;
      if (letter && row.letter !== letter) return false;
      if (!rowMatches(row, query)) return false;
      return true;
    });
  }, [rows, query, letter, examplesFilter]);

  const totalActive = rows.filter((r) => r.deleted_at === null).length;
  const withExamples = rows.filter((r) => r.deleted_at === null && r.examples.length > 0).length;
  const withoutExamples = rows.filter((r) => r.deleted_at === null && r.examples.length === 0).length;
  const softDeleted = rows.filter((r) => r.deleted_at !== null).length;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (status === "loading" || status === "idle") {
    return <p className="text-sm text-fg-muted animate-pulse" data-testid="vocab-loading">Lade Vokabeln…</p>;
  }
  if (status === "error") {
    return (
      <div
        role="alert"
        data-testid="vocab-load-error"
        className="bg-danger-soft border border-danger/40 rounded-lg px-3 py-2 text-sm text-danger"
      >
        {error ?? "Vokabeln konnten nicht geladen werden."}
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="admin-vocab-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat num={totalActive} label="Gesamt aktiv" testid="vocab-stat-active" />
        <Stat num={withExamples} label="Mit Beispielen" testid="vocab-stat-with" />
        <Stat num={withoutExamples} label="Ohne Beispiele" tone={withoutExamples > 0 ? "warn" : "muted"} testid="vocab-stat-without" />
        <Stat num={softDeleted} label="Gelöscht" tone="muted" testid="vocab-stat-deleted" />
      </div>

      <div className="flex items-center justify-end gap-2">
        {/*
          CSV-Export-Button (#83). Browser-natives Download dank
          `<a download>` — Session-Cookie wird automatisch mitgeschickt
          weil same-origin. Kein JS-Fetch, kein Blob-Handling.
        */}
        <a
          href="/api/v1/admin/vocab/export.csv"
          download
          data-testid="vocab-export-csv"
          className="min-h-[36px] inline-flex items-center px-3 rounded-lg text-xs font-semibold text-fg border border-outline hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          📥 CSV-Export
        </a>

        <DangerDialog
          title="Auf Original-Liste zurücksetzen?"
          description={
            <span>
              <strong>Alle Admin-Änderungen</strong> seit dem letzten Deploy werden verworfen:
              hinzugefügte Wörter, korrigierte Übersetzungen, geänderte Beispiele, soft-gelöschte Einträge. Die DB
              wird auf die <strong>371 Original-Wörter</strong> aus dem Repo zurückgesetzt.
              <br />
              <br />
              User-Fortschritt bleibt erhalten für alle Wörter, die in der Baseline sind — nur für
              admin-hinzugefügte Wörter werden Progress + History gewiped.
            </span>
          }
          confirmPhrase="RESET"
          confirmLabel="Auf Original zurücksetzen"
          onConfirm={async () => {
            setResetMessage(null);
            const result = await resetToBaseline();
            if (result.ok) {
              setResetMessage({
                kind: "success",
                text: `Auf Baseline zurückgesetzt: ${result.summary.vocabAfter} Wörter aktiv. ${result.summary.orphanProgress} Progress- und ${result.summary.orphanHistory} History-Rows für admin-hinzugefügte Wörter wurden entfernt.`,
              });
            } else {
              setResetMessage({ kind: "error", text: result.message });
            }
          }}
          trigger={
            <button
              type="button"
              data-testid="vocab-reset-open"
              className="min-h-[36px] px-3 rounded-lg text-xs font-semibold text-danger border border-danger/40 hover:bg-danger-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-danger"
            >
              ⚠ Auf Original zurücksetzen
            </button>
          }
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suche: englisch / deutsch / Abkürzung …"
          data-testid="vocab-search"
          className="w-full min-h-[40px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
        />
        <select
          value={letter}
          onChange={(e) => setLetter(e.target.value)}
          data-testid="vocab-letter-filter"
          className="min-h-[40px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg focus:outline-none focus:border-accent"
        >
          <option value="">Buchstabe: alle</option>
          {lettersInUse.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={examplesFilter}
          onChange={(e) => setExamplesFilter(e.target.value as ExamplesFilter)}
          data-testid="vocab-examples-filter"
          className="min-h-[40px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg focus:outline-none focus:border-accent"
        >
          <option value="all">Status: alle aktiven</option>
          <option value="with">Mit Beispielen</option>
          <option value="without">Ohne Beispiele</option>
          <option value="deleted">Gelöschte</option>
        </select>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          data-testid="vocab-add-open"
          className="min-h-[40px] px-4 rounded-lg bg-accent text-accent-on text-sm font-semibold hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          + Neues Wort
        </button>
      </div>

      {restoreError && (
        <div
          role="alert"
          data-testid="vocab-restore-error"
          className="bg-danger-soft border border-danger/40 rounded-lg px-3 py-2 text-sm text-danger"
        >
          {restoreError}
        </div>
      )}

      {resetMessage && (
        <div
          role="status"
          data-testid="vocab-reset-toast"
          className={`rounded-lg px-3 py-2 text-sm border ${
            resetMessage.kind === "success"
              ? "bg-success-soft border-success/40 text-success"
              : "bg-danger-soft border-danger/40 text-danger"
          }`}
        >
          {resetMessage.text}
        </div>
      )}

      {/* Table on >= sm; cards on mobile. */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-fg-faint text-[10px] uppercase tracking-wider">
              <th className="text-left py-2 px-2">L</th>
              <th className="text-left py-2 px-2">Englisch</th>
              <th className="text-left py-2 px-2">Deutsch</th>
              <th className="text-left py-2 px-2">Beispiele</th>
              <th className="text-left py-2 px-2">Geändert</th>
              <th className="text-right py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <VocabRow
                key={row.english}
                row={row}
                onEdit={() => setEditRow(row)}
                onDelete={async () => {
                  await softDelete(row.english);
                }}
                onRestore={async () => {
                  setRestoreError(null);
                  const result = await restore(row.english);
                  if (!result.ok) setRestoreError(result.message);
                }}
              />
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-4 text-fg-faint text-sm">
                  Keine Treffer für die aktuellen Filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card variant */}
      <div className="sm:hidden space-y-2">
        {pageRows.map((row) => (
          <VocabCard
            key={row.english}
            row={row}
            onEdit={() => setEditRow(row)}
            onDelete={async () => {
              await softDelete(row.english);
            }}
            onRestore={async () => {
              setRestoreError(null);
              const result = await restore(row.english);
              if (!result.ok) setRestoreError(result.message);
            }}
          />
        ))}
        {pageRows.length === 0 && (
          <p className="text-center py-4 text-fg-faint text-sm">Keine Treffer für die aktuellen Filter.</p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-fg-muted" data-testid="vocab-paginator">
        <span>
          Seite {safePage} von {totalPages} · {filtered.length} von {rows.length} angezeigt
        </span>
        <div className="flex gap-1">
          <PaginatorButton onClick={() => setPage(1)} disabled={safePage === 1}>
            «
          </PaginatorButton>
          <PaginatorButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1}>
            ‹
          </PaginatorButton>
          <PaginatorButton onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>
            ›
          </PaginatorButton>
          <PaginatorButton onClick={() => setPage(totalPages)} disabled={safePage === totalPages}>
            »
          </PaginatorButton>
        </div>
      </div>

      <VocabFormDialog open={addOpen} onOpenChange={setAddOpen} mode="create" />
      {editRow && (
        <VocabFormDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditRow(null);
          }}
          mode="edit"
          initial={editRow}
        />
      )}
    </section>
  );
}

function Stat({
  num,
  label,
  tone = "default",
  testid,
}: {
  num: number;
  label: string;
  tone?: "default" | "muted" | "warn";
  testid: string;
}) {
  const numClass =
    tone === "muted"
      ? "text-fg-muted"
      : tone === "warn"
        ? "text-warning"
        : "text-fg";
  return (
    <div className="bg-card border border-outline-soft rounded-lg px-3 py-2" data-testid={testid}>
      <div className={`text-xl font-bold font-mono ${numClass}`}>{num}</div>
      <div className="text-[10px] uppercase tracking-wider text-fg-faint mt-0.5">{label}</div>
    </div>
  );
}

function PaginatorButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="min-h-[32px] px-2.5 rounded border border-outline text-fg-soft hover:bg-card disabled:opacity-40 text-sm"
    >
      {children}
    </button>
  );
}

interface RowProps {
  row: AdminVocabRow;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  onRestore: () => Promise<void>;
}

function VocabRow({ row, onEdit, onDelete, onRestore }: RowProps): JSX.Element {
  const deleted = row.deleted_at !== null;
  return (
    <tr
      data-testid={`vocab-row-${row.english}`}
      className={`border-t border-outline-soft ${deleted ? "opacity-50" : ""}`}
    >
      <td className="py-2 px-2">
        <span className={`inline-block w-6 h-6 rounded text-xs font-bold font-mono text-center leading-6 ${deleted ? "bg-fg-faint/20 text-fg-faint" : "bg-accent text-accent-on"}`}>
          {row.letter}
        </span>
      </td>
      <td className="py-2 px-2">
        <span className={`font-semibold ${deleted ? "line-through" : "text-fg"}`}>{row.english}</span>
        {row.abbreviation && (
          <span className="ml-2 text-xs font-mono text-fg-faint">{row.abbreviation}</span>
        )}
        {deleted && (
          <span className="ml-2 text-[10px] uppercase tracking-wider bg-fg-faint/20 text-fg-muted px-1.5 py-0.5 rounded">
            Gelöscht
          </span>
        )}
      </td>
      <td className={`py-2 px-2 ${deleted ? "line-through text-fg-faint" : "text-fg-soft"}`}>
        {row.german}
      </td>
      <td className="py-2 px-2">
        <ExamplesBadge count={row.examples.length} />
      </td>
      <td className="py-2 px-2 text-xs text-fg-faint">{fmtRelative(row.updated_at)}</td>
      <td className="py-2 px-2 text-right whitespace-nowrap">
        <RowActions row={row} onEdit={onEdit} onDelete={onDelete} onRestore={onRestore} />
      </td>
    </tr>
  );
}

function VocabCard({ row, onEdit, onDelete, onRestore }: RowProps): JSX.Element {
  const deleted = row.deleted_at !== null;
  return (
    <div
      data-testid={`vocab-card-${row.english}`}
      className={`bg-card border border-outline-soft rounded-xl p-3 ${deleted ? "opacity-50" : ""}`}
    >
      <div className="flex items-start gap-2">
        <span className={`shrink-0 inline-block w-6 h-6 rounded text-xs font-bold font-mono text-center leading-6 ${deleted ? "bg-fg-faint/20 text-fg-faint" : "bg-accent text-accent-on"}`}>
          {row.letter}
        </span>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${deleted ? "line-through" : "text-fg"}`}>
            {row.english}
            {row.abbreviation && (
              <span className="ml-2 text-xs font-mono text-fg-faint">{row.abbreviation}</span>
            )}
          </div>
          <div className={`text-sm ${deleted ? "line-through text-fg-faint" : "text-fg-soft"}`}>{row.german}</div>
          <div className="mt-1 text-[11px] text-fg-faint flex gap-2 items-center">
            <ExamplesBadge count={row.examples.length} />
            <span>·</span>
            <span>{fmtRelative(row.updated_at)}</span>
          </div>
        </div>
      </div>
      <div className="mt-2 flex justify-end gap-1">
        <RowActions row={row} onEdit={onEdit} onDelete={onDelete} onRestore={onRestore} />
      </div>
    </div>
  );
}

function RowActions({ row, onEdit, onDelete, onRestore }: RowProps): JSX.Element {
  if (row.deleted_at !== null) {
    return (
      <button
        type="button"
        onClick={() => void onRestore()}
        data-testid={`vocab-restore-${row.english}`}
        className="text-accent text-xs font-semibold px-2 py-1 hover:underline"
      >
        Wiederherstellen
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={onEdit}
        data-testid={`vocab-edit-${row.english}`}
        className="text-accent text-xs font-semibold px-2 py-1 hover:underline"
      >
        Bearbeiten
      </button>
      <DangerDialog
        title="Wort löschen?"
        description={
          <span>
            Das Wort <strong>{row.english}</strong> wird soft-gelöscht — verschwindet aus dem Quiz,
            User-Fortschritt bleibt aber erhalten. Du kannst es jederzeit wiederherstellen.
          </span>
        }
        confirmPhrase={row.english}
        confirmLabel="Löschen"
        onConfirm={onDelete}
        trigger={
          <button
            type="button"
            data-testid={`vocab-delete-${row.english}`}
            className="text-danger text-xs font-semibold px-2 py-1 hover:underline"
          >
            Löschen
          </button>
        }
      />
    </>
  );
}

function ExamplesBadge({ count }: { count: number }): JSX.Element {
  const tone = count === 0 ? "warn" : "ok";
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold ${tone === "ok" ? "bg-success-soft text-success" : "bg-warning-soft text-warning"}`}
    >
      {count} Beispiel{count === 1 ? "" : "e"}
    </span>
  );
}
